import { PageBuilderSectionModel } from "@de-trustleap/shared-models";
import { createElementFromWidget } from './createElementFromWidget';
import { processBackgroundColor } from '../utils/processBackgroundColor';
import { lazyLoadObserver } from '../utils/intersectionObserver';

// 🔍 Hilfsfunktion zur Erkennung von Fullwidth-Widgets
function hasFullwidthWidget(section: PageBuilderSectionModel): boolean {
    return section.widgets?.some(w => ['footer', 'hero', 'banner'].includes(w.elementType ?? '')) ?? false;
}

export function createSectionElement(section: PageBuilderSectionModel, isLastSection = false): HTMLElement {
    const container = document.createElement('div');
    container.classList.add("lazy-section");
    applyLazyAnimationStyles(container, isLastSection);

    const fragment = document.createDocumentFragment();
    const isFullwidth = hasFullwidthWidget(section);

    const backgroundContainer = createBackgroundLayer(section);
    const contentContainer = isFullwidth ? null : createContentContainer(section);

    fragment.appendChild(backgroundContainer);
    if (contentContainer) fragment.appendChild(contentContainer);
    container.appendChild(fragment);

    // Lazy Load Observer
    lazyLoadObserver.observe(container, async () => {
        try {
            const widgetFragment = await renderWidgets(section);
            if (isFullwidth) container.appendChild(widgetFragment);
            else contentContainer?.appendChild(widgetFragment);

            container.style.minHeight = 'unset';
            container.style.opacity = '1';
            container.style.transform = 'translateY(0)';
        } catch (err) {
            console.error("Fehler beim Rendern der Widgets:", err);
        }
    });

    return container;
}


function applyLazyAnimationStyles(el: HTMLElement, isLast: boolean) {
    Object.assign(el.style, {
        position: 'relative',
        width: '100%',
        boxSizing: 'border-box',
        minHeight: isLast ? 'auto' : '300px',
        opacity: '0',
        transition: 'opacity 0.6s ease-out, transform 0.6s ease-out',
        transform: 'translateY(20px)',
    });
}

function createContentContainer(section: PageBuilderSectionModel): HTMLElement {
    const content = document.createElement('div');
    Object.assign(content.style, {
        display: 'flex',
        flexDirection: section.layout === 'row' ? 'row' : 'column',
        justifyContent: 'center',
        alignItems: 'center',
        flexWrap: 'nowrap',
        width: '100%',
        maxWidth: section.maxWidth ? `${section.maxWidth}px` : 'auto',
        margin: '0 auto',
        boxSizing: 'border-box',
        zIndex: '1',
        position: 'relative',
    });
    return content;
}

function createBackgroundLayer(section: PageBuilderSectionModel): HTMLElement {
    const bg = document.createElement('div');
    Object.assign(bg.style, {
        position: 'absolute',
        top: '0',
        left: '0',
        width: '100%',
        height: '100%',
        zIndex: '0',
    });

    const { backgroundColor, imageProperties, overlayColor } = section.background ?? {};

    if (backgroundColor) {
        bg.style.backgroundColor = processBackgroundColor(backgroundColor);
    }

    if (imageProperties?.url) {
        Object.assign(bg.style, {
            backgroundImage: `url(${imageProperties.url})`,
            backgroundSize: imageProperties.contentMode === 'fill' ? '100% 100%' :
                imageProperties.contentMode === 'contain' ? 'contain' : 'cover',
            backgroundRepeat: 'no-repeat',
            backgroundPosition: 'center',
        });
    }

    if (overlayColor) {
        const overlay = document.createElement('div');
        Object.assign(overlay.style, {
            position: 'absolute',
            top: '0',
            left: '0',
            width: '100%',
            height: '100%',
            pointerEvents: 'none',
            backgroundColor: processBackgroundColor(overlayColor),
        });
        bg.appendChild(overlay);
    }

    return bg;
}

async function renderWidgets(section: PageBuilderSectionModel): Promise<DocumentFragment> {
    const fragment = document.createDocumentFragment();
    if (Array.isArray(section.widgets)) {
        for (const widget of section.widgets) {
            const element = await createElementFromWidget(widget);
            if (element) fragment.appendChild(element);
        }
    }
    return fragment;
}
